/**
 * Core Web Vitals monitoring and reporting
 * Tracks LCP, FID, CLS, FCP, TTFB and sends to analytics
 */

import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';

// Send vitals to Google Analytics
function sendToGoogleAnalytics(metric: Metric) {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', metric.name, {
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      event_category: 'Web Vitals',
      event_label: metric.id,
      non_interaction: true,
    });
  }
}

// Send vitals to Microsoft Clarity
function sendToClarity(metric: Metric) {
  if (typeof window !== 'undefined' && (window as any).clarity) {
    (window as any).clarity('set', metric.name, Math.round(metric.value));
  }
}

// Send vitals to console in development
function logToConsole(metric: Metric) {
  if (import.meta.env.DEV) {
    console.log(`[Web Vitals] ${metric.name}:`, {
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
    });
  }
}

// Main handler for all metrics
function handleMetric(metric: Metric) {
  logToConsole(metric);
  sendToGoogleAnalytics(metric);
  sendToClarity(metric);
}

// Initialize Web Vitals monitoring
export function initWebVitals() {
  try {
    onCLS(handleMetric);  // Cumulative Layout Shift
    onFCP(handleMetric);  // First Contentful Paint
    onINP(handleMetric);  // Interaction to Next Paint (replaces FID)
    onLCP(handleMetric);  // Largest Contentful Paint
    onTTFB(handleMetric); // Time to First Byte
  } catch (error) {
    console.error('Failed to initialize Web Vitals:', error);
  }
}

// Get Web Vitals thresholds for rating
export const WEB_VITALS_THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 },
  INP: { good: 200, needsImprovement: 500 }, // Replaces FID
  CLS: { good: 0.1, needsImprovement: 0.25 },
  FCP: { good: 1800, needsImprovement: 3000 },
  TTFB: { good: 800, needsImprovement: 1800 },
};
